import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import './NotificationPopover.less';
import {
    Badge,
    Button,
    Empty,
    List,
    Popover,
    Space,
    Spin,
    Typography,
    message,
} from "antd";
import {
    BellOutlined,
    CheckOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    GET_POPUP_NOTIFICATIONS,
    UPDATE_POPUP_NOTIFICATION,
} from "./NotificationPopover.gql";
import ringer from "../../../assets/sound/mixkit-software-interface-start-2574.mp3";

const { Text, Title, Paragraph } = Typography;

type PopupNotification = {
    id: string;
    title: string;
    content: string;
    isRead: boolean;
    createdAt: string;
    updatedAt?: string | null;
};

type PopupNotificationsData = {
    popupNotifications: {
        popupNotifications: PopupNotification[];
        unreadCount: number;
    };
};

type UpdatePopupNotificationData = {
    updatePopupNotification: {
        status: string;
        id?: string | null;
    };
};

type UpdatePopupNotificationVariables = {
    id: string;
};

const formatDateTime = (value: string): string => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(date);
};

const NotificationPopover: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement>(
        new Audio(ringer)
    );

    const {
        data,
        loading,
        refetch,
        networkStatus,
    } = useQuery<PopupNotificationsData>(GET_POPUP_NOTIFICATIONS, {
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
        pollInterval: 15000,
    });

    const [updatePopupNotification] = useMutation<
        UpdatePopupNotificationData,
        UpdatePopupNotificationVariables
    >(UPDATE_POPUP_NOTIFICATION);

    const notifications = useMemo(
        () => data?.popupNotifications?.popupNotifications ?? [],
        [data]
    );

    const unreadCount =
        data?.popupNotifications?.unreadCount ?? 0;

    const refreshing = networkStatus === 4;

    const handleMarkAsRead = async (
        notification: PopupNotification
    ): Promise<void> => {
        if (notification.isRead || updatingId) {
            return;
        }

        try {
            setUpdatingId(notification.id);

            const result = await updatePopupNotification({
                variables: {
                    id: notification.id,
                },
            });

            if (
                result.data?.updatePopupNotification?.status !==
                "SUCCESS"
            ) {
                throw new Error(
                    "Unable to update notification."
                );
            }

            await refetch();
        } catch (error) {
            console.error(
                "Failed to mark notification as read:",
                error
            );

            message.error(
                "Failed to mark the notification as read."
            );
        } finally {
            setUpdatingId(null);
        }
    };

    const handleRefresh = async (): Promise<void> => {
        try {
            await refetch();
        } catch (error) {
            console.error(
                "Failed to refresh notifications:",
                error
            );

            message.error(
                "Failed to refresh notifications."
            );
        }
    };

    const content = (
        <div style={{ width: 420, maxWidth: "80vw" }}>
            <Space
                align="center"
                style={{
                    width: "100%",
                    justifyContent: "space-between",
                    marginBottom: 12,
                }}
            >
                <div>
                    <Title
                        level={5}
                        style={{ margin: 0 }}
                    >
                        Notifications
                    </Title>

                    <Text type="secondary">
                        {unreadCount} unread
                    </Text>
                </div>

                <Button
                    type="text"
                    icon={<ReloadOutlined />}
                    loading={refreshing}
                    onClick={handleRefresh}
                    aria-label="Refresh notifications"
                />
            </Space>

            <Spin spinning={loading && !data}>
                {notifications.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No notifications"
                    />
                ) : (
                    <List
                        itemLayout="vertical"
                        dataSource={notifications}
                        style={{
                            maxHeight: 480,
                            overflowY: "auto",
                        }}
                        renderItem={(notification) => (
                            <List.Item
                                key={notification.id}
                                style={{
                                    padding: "12px 4px",
                                    opacity:
                                        notification.isRead
                                            ? 0.65
                                            : 1,
                                }}
                                actions={[
                                    notification.isRead ? (
                                        <Text
                                            key="read"
                                            type="secondary"
                                        >
                                            <CheckOutlined /> Read
                                        </Text>
                                    ) : (
                                        <Button
                                            key="mark-read"
                                            type="link"
                                            size="small"
                                            loading={
                                                updatingId ===
                                                notification.id
                                            }
                                            disabled={
                                                updatingId !== null &&
                                                updatingId !==
                                                    notification.id
                                            }
                                            onClick={() =>
                                                handleMarkAsRead(
                                                    notification
                                                )
                                            }
                                        >
                                            Mark as read
                                        </Button>
                                    ),
                                ]}
                            >
                                <List.Item.Meta
                                    title={
                                        <Space size={8}>
                                            {!notification.isRead && (
                                                <Badge status="processing" />
                                            )}

                                            <Text strong>
                                                {notification.title}
                                            </Text>
                                        </Space>
                                    }
                                    description={
                                        <Text type="secondary">
                                            {formatDateTime(
                                                notification.createdAt
                                            )}
                                        </Text>
                                    }
                                />

                                <Paragraph
                                    style={{
                                        marginBottom: 0,
                                        whiteSpace: "pre-line",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {notification.content}
                                </Paragraph>
                            </List.Item>
                        )}
                    />
                )}
            </Spin>
        </div>
    );

    const unreadCountRef = useRef(0);

    useEffect(() => {
        audioRef.current.preload = "auto";
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep the ref in sync with the latest unread count
    useEffect(() => {
        unreadCountRef.current = unreadCount;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unreadCount]);

    // Create ONE interval only
    useEffect(() => {
        const interval = setInterval(() => {
            if (unreadCountRef.current > 0) {
                console.log(unreadCountRef.current);
                audioRef.current.currentTime = 0;

                audioRef.current.play().catch((err) => {
                    console.log(err);
                });
            }
        }, 15000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="notification-popover-container">
            <Popover
                trigger="click"
                placement="bottomRight"
                open={open}
                onOpenChange={setOpen}
                content={content}
                overlayStyle={{ paddingTop: 8 }}
            >
                <Badge
                    count={unreadCount}
                    overflowCount={99}
                    size="small"
                >
                    <Button
                        type="text"
                        shape="circle"
                        icon={<BellOutlined />}
                        aria-label="Notifications"
                    />
                </Badge>
            </Popover>
        </div>
        
    );
};

export default NotificationPopover;
